import { Attachment } from "../components/FileAttachments/FileAttachments";

export type AppType = "FAQ" | "Manuals" | "ALL";

export const isValidAppType = (appType: unknown): boolean => {
    return appType === "FAQ" || appType === "Manuals" || appType === "ALL";
};

export const RetrievalMode = Object.freeze({
    Hybrid: "hybrid",
    Vectors: "vectors",
    Text: "text"
} as const);
export type RetrievalMode = (typeof RetrievalMode)[keyof typeof RetrievalMode];

export const enum RetrievalReasoningEffort {
    Auto = "auto",
    Minimal = "minimal",
    Low = "low",
    Medium = "medium"
}

export const enum ChatModel {
    GPT_41 = "gpt-4.1",
    GPT_41_MINI = "gpt-4.1-mini",
    GPT_5 = "gpt-5",
    GPT_5_MINI = "gpt-5-mini",
    GPT_5_NANO = "gpt-5-nano",
    GPT_54 = "gpt-5.4",
    GPT_54_MINI = "gpt-5.4-mini",
    GPT_54_NANO = "gpt-5.4-nano",
    GPT_56_LUNA = "gpt-5.6-luna",
    GPT_6_LUNA = "gpt-6-luna",
    GPT_61_SOL = "gpt-6.1-sol",
    /** Claude models deployed in Microsoft Foundry (Anthropic Messages API). */
    CLAUDE_HAIKU_55 = "claude-haiku-5-5",
    CLAUDE_SONNET_55 = "claude-sonnet-5-5"
}

/** Every selectable chat model; used to validate persisted user settings. */
export const CHAT_MODELS: readonly ChatModel[] = [
    ChatModel.GPT_41,
    ChatModel.GPT_41_MINI,
    ChatModel.GPT_5,
    ChatModel.GPT_5_MINI,
    ChatModel.GPT_5_NANO,
    ChatModel.GPT_54,
    ChatModel.GPT_54_MINI,
    ChatModel.GPT_54_NANO,
    ChatModel.GPT_56_LUNA,
    ChatModel.GPT_6_LUNA,
    ChatModel.GPT_61_SOL,
    ChatModel.CLAUDE_HAIKU_55,
    ChatModel.CLAUDE_SONNET_55
];

export const isKnownChatModel = (value: unknown): value is ChatModel => typeof value === "string" && (CHAT_MODELS as readonly string[]).includes(value);

export const enum Locale {
    JA = "ja",
    EN = "en"
}

export const enum ReasoningEffort {
    None = "none",
    Minimal = "minimal",
    Low = "low",
    Medium = "medium",
    High = "high"
}

export const GPT5_REASONING_EFFORTS = [ReasoningEffort.Minimal, ReasoningEffort.Low, ReasoningEffort.Medium, ReasoningEffort.High] as const;
export const GPT54_REASONING_EFFORTS = [ReasoningEffort.None, ReasoningEffort.Low, ReasoningEffort.Medium, ReasoningEffort.High] as const;
export const GPT56_REASONING_EFFORTS = [ReasoningEffort.None, ReasoningEffort.Low, ReasoningEffort.Medium, ReasoningEffort.High] as const;
export const GPT6_REASONING_EFFORTS = [ReasoningEffort.None, ReasoningEffort.Low, ReasoningEffort.Medium, ReasoningEffort.High] as const;
/** Claude: "none" disables thinking; the others map to adaptive thinking at the matching effort level. */
export const CLAUDE_REASONING_EFFORTS = [ReasoningEffort.None, ReasoningEffort.Low, ReasoningEffort.Medium, ReasoningEffort.High] as const;

export const isGPT6Model = (model: string): boolean => model.startsWith("gpt-6");
export const isGPT56Model = (model: string): boolean => model.startsWith("gpt-5.6");
export const isGPT54Model = (model: string): boolean => model.startsWith("gpt-5.4");
export const isGPT5SeriesModel = (model: string): boolean => model.startsWith("gpt-5");
/** Claude models deployed in Microsoft Foundry (served through the Anthropic Messages API). */
export const isClaudeModel = (model: string): boolean => model.startsWith("claude");
/** Models that take `reasoning_effort` instead of `temperature` (gpt-5.x, gpt-6.x and Claude). */
export const isReasoningModel = (model: string): boolean => isGPT5SeriesModel(model) || isGPT6Model(model) || isClaudeModel(model);

export const getReasoningEffortsForModel = (model: string): readonly ReasoningEffort[] => {
    if (isClaudeModel(model)) return CLAUDE_REASONING_EFFORTS;
    if (isGPT6Model(model)) return GPT6_REASONING_EFFORTS;
    if (isGPT56Model(model)) return GPT56_REASONING_EFFORTS;
    if (isGPT54Model(model)) return GPT54_REASONING_EFFORTS;
    if (isGPT5SeriesModel(model)) return GPT5_REASONING_EFFORTS;
    return [];
};

export type ChatAppRequestOverrides = {
    chat_model?: ChatModel;
    reasoning_effort?: ReasoningEffort;
    retrieval_reasoning_effort?: RetrievalReasoningEffort;
    only_citations?: boolean;
    retrieval_mode?: RetrievalMode;
    cosmosdb_retrieval_mode?: RetrievalMode;
    language?: string;
    semantic_ranker?: boolean;
    semantic_captions?: boolean;
    exclude_category?: string;
    top?: number;
    temperature?: number;
    prompt_template?: string;
    prompt_template_prefix?: string;
    prompt_template_suffix?: string;
    suggest_followup_questions?: boolean;
    enable_OCR?: boolean;
    product_filter?: any;
    doctype_filter?: string;
    sub_product_filter?: any;
    product_version?: any;
    version_index?: string;
    template_keys?: any;
    templateContents?: any;
    faq_version?: string;
    group_ids?: string;
    sub_group_ids?: string;
    templateId?: string;
    extractedQuestion?: string;
    latest_user_request?: string;
    promptInstructions?: string;
    chat_summary_title?: string;
    file_attachment_ids?: Array<any>;
    current_question_file_attachment_ids?: Array<any>;
    use_agentic_knowledgebase?: boolean;
    fromHistory?: boolean;
    sessionId?: string | null;
    versions?: Record<string, any>;
    question_id?: string;
    has_question_id?: boolean;
    old_chat_id?: string;
    agentic_retrieval_output_size?: number;
    agentic_retrieval_document_count?: number;
    agentic_retrieval_source_document_count?: number;
    edited_question_id?: string | null;
    is_chat_shared?: boolean;
    source_shared_session_id?: string | null;
    all_product_selection_meta?: {
        source?: "auto" | "manual" | "continue_all";
        autoSelectionSnapshot?: Record<string, any> | null;
    } | null;
    combinedAppTypeResponse?: boolean;
    isAppTypeIntegrated?: boolean;
    isRegenerated?: boolean;
    skip_save?: boolean;
    skip_validation?: boolean;
    keywords?: string;
};

export type ResponseMessage = {
    content: string;
    role: string;
};
export type ResponseContext = {
    conv_id?: string;
    faqVersion: string | null;
    thoughts?: string | null;
    has_thoughts?: boolean;
    data_points: DataPoints[];
    followup_questions: string[] | null;
    keywords: string | null;
    feedback?: FeedbackResponse;
    chat_model: string | null;
    reasoning_effort: string | null;
    retrieval_reasoning_effort: RetrievalReasoningEffort | null;
    product_filter: { manual_key: string; faq_key: string } | null;
    sub_product_filter: { manual_sub_key: string; faq_sub_key: string } | null;
    app_type: string | null;
    language: string | null;
    document_category?: string | null;
    feedback_corp_id: string | null;
    feedback_user: string | null;
    feedbackgiven: boolean;
    question_validation?: { is_vague: boolean; type: "greeting" | "vague" | "off_topic"; message: string } | null;
    feedbacktype: string | null;
    corpId?: string | null;
    userId?: string | null;
    productVersion: { manual_version_key: string; faq_version_key: string } | null;
    versionIndex: string | null;
    file_mapping: object | null;
    question_id: string | null;
    has_question_id: boolean | null;
    product_group_ids: string | null;
    sub_product_group_ids: string | null;
    groupId?: string | null;
    title?: string | null;
    conversationDate?: string | null;
    reasoning_summary?: string | null;
    precheck_selection_decision?: AllProductPrecheckSelectionDecision | null;
};

export interface AllProductPrecheckSelectionDecision {
    action: "auto_select" | "manual_select";
    // The model/heuristic's raw pick, before any customer PTF-config override.
    selected_display_label?: string | null;
    selected_product_label?: string | null;
    selected_version_label?: string | null;
    selected_sub_product_label?: string | null;
    selected_product_indexes?: string[] | null;
    selected_version_indexes?: string[] | null;
    selected_sub_product_indexes?: string[] | null;
    selected_score?: number | null;
    selected_citation_count?: number | null;
    // The effective pick to actually use — equals selected_* unless the customer's
    // registered PTF product/version config overrode it (see customer_config_applied).
    ptf_selected_display_label?: string | null;
    ptf_selected_product_label?: string | null;
    ptf_selected_version_label?: string | null;
    ptf_selected_sub_product_label?: string | null;
    ptf_selected_product_indexes?: string[] | null;
    ptf_selected_version_indexes?: string[] | null;
    ptf_selected_sub_product_indexes?: string[] | null;
    ptf_selected_score?: number | null;
    ptf_selected_citation_count?: number | null;
    // True when the customer's registered product/version config overrode the model's original pick.
    customer_config_applied?: boolean;
    // True when the corp has a PTF registration on file for this product at all.
    customer_config_registered?: boolean;
    // The customer's registered version major (e.g. "12"), when registered.
    customer_config_version_major?: string | null;
    // The version major actually used (ptf_selected_*), for comparing against customer_config_version_major.
    customer_config_effective_version_major?: string | null;
    // "matched" | "config_lower" | "config_higher" | "unknown" | "not_registered" | "registered_no_version" | "lookup_skipped" | "lookup_failed" — how the
    // registered version compares to what was actually used when it isn't among the candidates.
    customer_config_comparison?: string | null;
    // All product candidates the precheck selector chose from (auto_select only).
    candidates?: AllProductPrecheckCandidate[] | null;
    reason?: string | null;
    reason_code?: string | null;
}

export interface AllProductPrecheckCandidate {
    display_label: string;
    product_label?: string | null;
    version_label?: string | null;
    sub_product_label?: string | null;
    product_indexes?: string[] | null;
    version_indexes?: string[] | null;
    sub_product_indexes?: string[] | null;
    citation_count?: number | null;
    average_score?: number | null;
    max_score?: number | null;
}

export type ChatAppResponseOrError = {
    output_text?: string;
    type?: string;
    delta?: string;
    context?: ResponseContext;
    session_state?: any;
    error?: string;
};

export type ChatAppResponse = {
    output_text: string;
    context: ResponseContext;
    conversation_date?: string;
    session_state?: any;
};

export type ChatAppRequestContext = {
    overrides?: ChatAppRequestOverrides;
};

export type ChatAppRequest = {
    messages: ResponseMessage[];
    context?: ChatAppRequestContext;
    stream?: boolean;
    session_state: any;
};

export type SaveResponse = {
    feedbacks: object;
    shouldStream?: boolean;
};

export type Feedback = {
    chat_id?: string;
    user_id?: string;
    text: string;
    type: string;
    options?: number[];
};

export type AdminFeedback = {
    chat_id?: string;
    user_id?: string;
    text: string;
    type: string;
    options?: number[];
};
export type FeedbackResponse = {
    status?: string | boolean;
    category?: number | string;
    c_date?: string;
    data?: { text: string; type?: Array<number>; time?: string };
};

export type DataPoints = {
    uploaddir_field: string;
    sourcepage_field: string;
    content_field: string;
    search_score?: string;
    search_reranker_score?: string;
    doc_url?: string;
    doctype?: string;
    pdf_image_urls?: string[];
    pdf_images?: string[];
    hasPassword: boolean;
    source_kind?: string;
    product_index?: string[] | string;
    sub_product_index?: string[] | string;
    version?: string[] | string;
    version_name?: string[] | string;
    start_version?: string[] | string | number;
    end_version?: string[] | string | number;
    sub_product_name?: string[] | string;
    product_name?: string;
    product_full_name?: string;
};

export type DeveloperOptions = {
    top: number;
    chat_model: ChatModel;
    reasoning_effort: ReasoningEffort;
    retrieval_mode: RetrievalMode;
    cosmosdb_retrieval_mode?: RetrievalMode;
    only_citations: boolean;
    semantic_ranker: boolean;
    semantic_captions: boolean;
    suggest_followup_questions: boolean;
    use_agentic_knowledgebase?: boolean;
    agentic_retrieval_output_size?: number;
    agentic_retrieval_document_count?: number;
    agentic_retrieval_source_document_count?: number;
};

export type SessionInfo = {
    title?: string;
    conversation_date?: string;
    session_id?: string;
    corp_id?: string;
    user_id?: string;
    group?: number;
    chat_summary_title?: string;
};

export type SessionSummaryTopic = {
    question_id: string;
    topic_label: string;
};

export type SessionSummaryLocalized = {
    overall_summary: string;
    topics: SessionSummaryTopic[];
};

export type SessionSummaryResponse = {
    session_id: string;
    overall_summary: string;
    summary_generated_at: string;
    summary_error: string;
    conversation_count: number;
    topics: SessionSummaryTopic[];
    summary_language: string;
    summaries: Record<string, SessionSummaryLocalized>;
};

export type ProductFilter = {
    faq_key?: string;
    manual_key?: string;
};

export type DataOptions = {
    chat_model: ChatModel;
    doctype_filter: string;
    product_filter?: ProductFilter;
    shared_chat?: boolean;
    sub_product_filter?: string;
    app_type?: string;
    productVersion?: string;
    versionIndex?: string;
    faqVersion?: string;
    product?: Record<string, unknown>;
    subProduct?: Record<string, unknown>;
    product_version?: Record<string, unknown>;
};

export interface YearData {
    year: string;
    sessions: {
        title: string;
        month: number;
        monthName: string;
        records: SessionInfo[];
    }[];
}

// indicates the chat is a new or a history session
export type ChatSessionMode = "chat" | "history";

export const enum InstructionLabel {
    JA = "説明書",
    EN = "Instructions"
}

export type QuestionDetails = {
    question: string;
    templateId: string;
    extractedQuestion: string;
    isFilesAttached: boolean;
    filesAttached: Array<Attachment>;
    combinedAppTypeResponse: boolean;
    question_id?: string;
    conversation_date?: string;
    parentMessageId?: string;
    rootId?: string;
    edited_version?: number | string | null;
    isRegenerate?: boolean;
};

export type QuestionInfo = {
    parentMessageId: string;
    version: string;
};

export type ResultItem = [QuestionInfo, ...any[]];
export type VersionBuckets = Record<string, ResultItem[]>;
export type Histories = Record<string, VersionBuckets>;
